import React, { useMemo, useState } from 'react';
import { ArrowLeft, Bookmark, ChevronDown, ChevronRight, ListFilter, Plus, Trash2, UploadCloud } from 'lucide-react';
import { MasterList, SelectedTrickReference, TournamentTrick, User } from '../types';
import SubmitDeckModal from './SubmitDeckModal';

interface TournamentFinalSubmissionProps {
  user: User;
  onBack: () => void;
}

const FINAL_SUBMISSION_LIST_ID = 'cko2026';
const FINAL_SUBMISSION_DECK_NAME = '2026 CKO 决赛提交表';
const getFinalBonusForScore = (score: number) => {
  if (score === 13) return 30;
  if (score === 14) return 100;
  if (score === 15) return 225;
  return 0;
};

const TournamentFinalSubmission: React.FC<TournamentFinalSubmissionProps> = ({ user, onBack }) => {
  const config = user.tournamentData!;
  const availableMasterLists = useMemo(
    () => config.masterLists.filter(list => list.id === FINAL_SUBMISSION_LIST_ID),
    [config.masterLists]
  );
  const [selectedMasterListId, setSelectedMasterListId] = useState<string>(
    availableMasterLists[0]?.id || FINAL_SUBMISSION_LIST_ID
  );
  const [selectedTricks, setSelectedTricks] = useState<SelectedTrickReference[]>([]);
  const [previewInitialScore, setPreviewInitialScore] = useState<string>('0');
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [expandedScores, setExpandedScores] = useState<Record<number, boolean>>({});
  const [draggingSelectedIndex, setDraggingSelectedIndex] = useState<number | null>(null);
  const [selectedDropIndex, setSelectedDropIndex] = useState<number | null>(null);

  const selectedMasterList = useMemo<MasterList | undefined>(
    () => availableMasterLists.find(list => list.id === selectedMasterListId) || availableMasterLists[0],
    [availableMasterLists, selectedMasterListId]
  );

  const sourceTrickGroups = useMemo(() => {
    if (!selectedMasterList) return [];

    const groups: Record<number, TournamentTrick[]> = {};
    selectedMasterList.tricks
      .filter(trick => !selectedTricks.some(reference => reference.masterListId === selectedMasterList.id && reference.trickId === trick.id))
      .forEach(trick => {
        if (!groups[trick.score]) groups[trick.score] = [];
        groups[trick.score].push(trick);
      });

    return Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b)
      .map(score => ({ score, tricks: groups[score] }));
  }, [selectedMasterList, selectedTricks]);

  const selectedTrickDetails = useMemo(
    () => selectedTricks
      .map(reference => {
        const list = config.masterLists.find(item => item.id === reference.masterListId);
        const trick = list?.tricks.find(item => item.id === reference.trickId);
        return trick ? { reference, trick } : null;
      })
      .filter((item): item is { reference: SelectedTrickReference; trick: TournamentTrick } => Boolean(item)),
    [config.masterLists, selectedTricks]
  );

  const selectedTrickScoreSummary = useMemo(() => {
    const summary = new Map<number, number>();
    let totalScore = 0;
    let squaredScore = 0;
    let bonusScore = 0;

    selectedTrickDetails.forEach(({ trick }) => {
      totalScore += trick.score;
      squaredScore += Math.pow(trick.score, 2);
      bonusScore += getFinalBonusForScore(trick.score);
      summary.set(trick.score, (summary.get(trick.score) || 0) + 1);
    });

    const breakdown = Array.from(summary.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([score, count]) => ({
        score,
        count,
        squaredSubtotal: Math.pow(score, 2) * count,
        bonusSubtotal: count * getFinalBonusForScore(score)
      }));

    return {
      totalScore,
      squaredScore,
      bonusScore,
      allSuccessBonusScore: totalScore,
      breakdown
    };
  }, [selectedTrickDetails]);

  const handleAddTrick = (trick: TournamentTrick) => {
    if (!selectedMasterList) return;
    if (selectedTricks.length >= 30) {
      alert('Final submission is limited to 30 tricks. / 决赛提交最多只能选择 30 个招式。');
      return;
    }
    setSelectedTricks(prev => [
      ...prev,
      { masterListId: selectedMasterList.id, trickId: trick.id }
    ]);
  };

  const handleRemoveTrick = (index: number) => {
    setSelectedTricks(prev => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const commitSelectedTrickReorder = (draggedIndex: number, rawDropIndex: number) => {
    if (
      draggedIndex < 0 ||
      rawDropIndex < 0 ||
      draggedIndex >= selectedTricks.length ||
      rawDropIndex > selectedTricks.length
    ) {
      return;
    }

    const targetIndex = draggedIndex < rawDropIndex ? rawDropIndex - 1 : rawDropIndex;
    if (draggedIndex === targetIndex) {
      return;
    }

    setSelectedTricks(previous => {
      const next = [...previous];
      const [draggedItem] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, draggedItem);
      return next;
    });
  };

  const toggleScoreGroup = (score: number) => {
    setExpandedScores(previous => ({
      ...previous,
      [score]: !(previous[score] ?? false)
    }));
  };

  const resetSelectedDragState = () => {
    setDraggingSelectedIndex(null);
    setSelectedDropIndex(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-2 md:p-4 flex flex-col h-full text-white pt-10 overflow-y-auto custom-scrollbar relative">
      <div className="flex items-center justify-between mb-4 shrink-0">
        <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft size={20} /> <span className="hidden sm:inline font-orbitron uppercase tracking-widest text-xs">Back</span>
        </button>
        <h2 className="text-xl md:text-2xl font-orbitron font-bold neon-glow-blue uppercase tracking-widest">
          Final Submission
        </h2>
        <div className="w-8"></div>
      </div>

      <div className="mb-4 rounded-2xl border border-blue-500/20 bg-zinc-900/70 p-4">
        <div className="text-lg font-orbitron text-blue-400">Points Tournament Final Submission / 积分赛决赛招式提交</div>
        <div className="mt-2 text-sm text-zinc-400">
          Select submission-only tricks here. This will not change the Finals round setup in tournament settings.
          <br />
          在这里选择的招式仅用于提交，不会影响积分赛设置里的正式决赛招式配置。
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="text-sm font-orbitron text-zinc-200 tracking-widest uppercase">Source List / 选择总表</div>
              <div className="text-[10px] font-orbitron text-cyan-300 uppercase tracking-widest">仅开放 2026 CKO</div>
            </div>
            <label className="text-[10px] text-zinc-500 font-orbitron uppercase tracking-widest block mb-1 flex items-center gap-2">
              <ListFilter size={12} /> Available List / 可用总表
            </label>
            <select
              value={selectedMasterList?.id || FINAL_SUBMISSION_LIST_ID}
              onChange={event => setSelectedMasterListId(event.target.value)}
              className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-xs text-yellow-500 font-rajdhani outline-none focus:border-yellow-500"
            >
              {availableMasterLists.map(list => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
            <div className="flex items-center justify-between gap-3 mb-3 shrink-0">
              <div className="text-sm font-orbitron text-zinc-200 tracking-widest uppercase">Choose Tricks / 选择招式</div>
              <div className="text-[10px] font-orbitron text-yellow-400 uppercase tracking-widest">展示全部等级，点击展开</div>
            </div>

            <div className="space-y-3">
              {sourceTrickGroups.length === 0 ? (
                <div className="rounded-xl border border-zinc-800 bg-black/20 px-4 py-5 text-sm text-zinc-500">
                  No more eligible tricks in this list. / 当前总表里没有更多可选招式。
                </div>
              ) : (
                sourceTrickGroups.map(({ score, tricks }) => {
                  const isExpanded = expandedScores[score] ?? false;
                  return (
                    <div key={score} className="rounded-xl border border-zinc-800 bg-black/20 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleScoreGroup(score)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="bg-yellow-600/80 text-black font-orbitron font-bold px-2 py-0.5 rounded text-[10px]">{score} PTS</div>
                          <div className="text-sm font-rajdhani text-zinc-300">{tricks.length} tricks / 个招式</div>
                        </div>
                        {isExpanded ? <ChevronDown size={16} className="text-yellow-300 shrink-0" /> : <ChevronRight size={16} className="text-yellow-300 shrink-0" />}
                      </button>

                      {isExpanded && (
                        <div className="p-3 pt-0 grid grid-cols-1 xl:grid-cols-2 gap-2 border-t border-zinc-800/80">
                          {tricks.map(trick => (
                            <div key={trick.id} className="mt-3 flex items-center justify-between rounded-xl border border-zinc-800 bg-black/30 px-3 py-2 gap-3">
                              <div className="min-w-0">
                                <div className="font-rajdhani text-base text-white whitespace-pre-wrap break-words leading-snug">{trick.name}</div>
                                <div className="text-[10px] text-zinc-500 font-orbitron uppercase tracking-widest">{trick.score} pts</div>
                              </div>
                              <button
                                onClick={() => handleAddTrick(trick)}
                                className="shrink-0 rounded-lg border border-green-500/40 bg-green-500/10 px-3 py-2 text-green-300 hover:bg-green-500/20 transition-colors flex items-center gap-2 text-xs font-orbitron"
                              >
                                <Plus size={14} /> Add
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
        </div>

        <div className="w-full flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <div className="text-sm font-orbitron text-zinc-200 tracking-widest uppercase">Selected Tricks / 已选招式</div>
            <div className="text-xs font-orbitron text-yellow-400">{selectedTricks.length} / 30</div>
          </div>

          <div className="space-y-2 min-h-[220px]">
            {selectedTrickDetails.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-700 bg-black/20 px-4 py-5 text-sm text-zinc-500">
                Choose tricks from the list above to prepare your final submission. / 从上方总表中选择招式，准备提交决赛表。
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-[10px] font-orbitron text-zinc-500 uppercase tracking-widest">
                  Drag to reorder / 拖动可调整顺序
                </div>
                {selectedTrickDetails.map(({ trick }, index) => (
                  <React.Fragment key={`${trick.id}-${index}`}>
                    {draggingSelectedIndex !== null && selectedDropIndex === index ? (
                      <div className="h-2 rounded-full border border-cyan-500/60 bg-cyan-500/20" />
                    ) : null}
                    <div
                      draggable
                      onDragStart={event => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('application/x-kendama-selected-index', String(index));
                        setDraggingSelectedIndex(index);
                        setSelectedDropIndex(index);
                      }}
                      onDragEnd={resetSelectedDragState}
                      onDragOver={event => {
                        event.preventDefault();
                        if (selectedDropIndex !== index) {
                          setSelectedDropIndex(index);
                        }
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        const draggedIndex = parseInt(
                          event.dataTransfer.getData('application/x-kendama-selected-index'),
                          10
                        );
                        if (!Number.isNaN(draggedIndex)) {
                          commitSelectedTrickReorder(draggedIndex, index);
                        }
                        resetSelectedDragState();
                      }}
                      className={`flex items-center justify-between rounded-xl border px-3 py-2 gap-3 transition-colors ${
                        draggingSelectedIndex === index
                          ? 'border-cyan-500/50 bg-cyan-500/10 opacity-60'
                          : 'border-zinc-800 bg-black/30'
                      }`}
                    >
                      <div className="min-w-0 flex items-center gap-3">
                        <div className="text-cyan-300/80 text-sm font-orbitron cursor-grab active:cursor-grabbing">::</div>
                        <div className="min-w-0">
                          <div className="text-[10px] font-orbitron text-zinc-500 uppercase tracking-widest">#{index + 1}</div>
                          <div className="font-rajdhani text-base text-white whitespace-pre-wrap break-words leading-snug">{trick.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs font-orbitron text-yellow-400">{trick.score} PTS</span>
                        <button
                          onClick={() => handleRemoveTrick(index)}
                          className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
                {draggingSelectedIndex !== null && selectedDropIndex === selectedTrickDetails.length ? (
                  <div className="h-2 rounded-full border border-cyan-500/60 bg-cyan-500/20" />
                ) : null}
                <div
                  onDragOver={event => {
                    event.preventDefault();
                    if (selectedDropIndex !== selectedTrickDetails.length) {
                      setSelectedDropIndex(selectedTrickDetails.length);
                    }
                  }}
                  onDrop={event => {
                    event.preventDefault();
                    const draggedIndex = parseInt(
                      event.dataTransfer.getData('application/x-kendama-selected-index'),
                      10
                    );
                    if (!Number.isNaN(draggedIndex)) {
                      commitSelectedTrickReorder(draggedIndex, selectedTrickDetails.length);
                    }
                    resetSelectedDragState();
                  }}
                  className="h-2"
                />
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-zinc-800 shrink-0">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[10px] text-zinc-500 font-orbitron uppercase tracking-widest flex items-center gap-2">
                <Bookmark size={12} /> Final Score Preview / 决赛得分构成
              </div>
              <div className="text-xl font-rajdhani font-black text-yellow-500 neon-glow-blue">
                {selectedTrickScoreSummary.squaredScore + selectedTrickScoreSummary.bonusScore + selectedTrickScoreSummary.allSuccessBonusScore + parseInt(previewInitialScore || '0', 10)} PTS
              </div>
            </div>
            {selectedTrickScoreSummary.breakdown.length > 0 && (
              <div className="mb-3 space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-md border border-green-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                    <div className="text-zinc-500 mb-1">初始分 / Initial</div>
                    <div className="flex items-center mt-1">
                      <input
                        type="number"
                        value={previewInitialScore}
                        onChange={event => setPreviewInitialScore(event.target.value)}
                        className="w-full bg-black/50 border border-zinc-700 rounded text-green-400 text-sm px-1 py-0.5 outline-none focus:border-green-500"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="rounded-md border border-cyan-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                    <div className="text-zinc-500 mb-1">乘算分 / Squared</div>
                    <div className="text-cyan-400 text-sm mt-1">{selectedTrickScoreSummary.squaredScore} PTS</div>
                  </div>
                  <div className="rounded-md border border-amber-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                    <div className="text-zinc-500 mb-1">奖励分 / Bonus</div>
                    <div className="text-amber-400 text-sm mt-1">{selectedTrickScoreSummary.bonusScore} PTS</div>
                  </div>
                  <div className="rounded-md border border-fuchsia-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                    <div className="text-zinc-500 mb-1">全成追加 / Full Mark</div>
                    <div className="text-fuchsia-400 text-sm mt-1">+{selectedTrickScoreSummary.allSuccessBonusScore} PTS</div>
                  </div>
                </div>
                <div className="rounded-md border border-zinc-700 bg-black/20 px-3 py-2 text-[10px] font-rajdhani text-zinc-400">
                  最终得分 = 初始分({parseInt(previewInitialScore || '0', 10)}) + 乘算分({selectedTrickScoreSummary.squaredScore}) + 奖励分({selectedTrickScoreSummary.bonusScore})；若全成，额外再加 {selectedTrickScoreSummary.allSuccessBonusScore} 分。
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedTrickScoreSummary.breakdown.map(({ score, count, squaredSubtotal, bonusSubtotal }) => (
                    <div
                      key={score}
                      className="rounded-md border border-yellow-500/20 bg-black/30 px-2 py-1 text-[10px] font-orbitron tracking-wide text-zinc-300"
                    >
                      {score}PTS x {count} = {squaredSubtotal}{bonusSubtotal > 0 ? ` (+${bonusSubtotal})` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                if (selectedTricks.length === 0) {
                  alert('Please select at least one final trick before submitting. / 请先至少选择一个决赛招式再提交。');
                  return;
                }
                setIsSubmitModalOpen(true);
              }}
              className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600/30 text-blue-400 rounded-lg hover:bg-blue-600/50 transition-colors font-orbitron text-xs tracking-widest border border-blue-500/50"
            >
              <UploadCloud size={14} /> Submit Final / 提交决赛表
            </button>
          </div>
        </div>
      </div>

      <SubmitDeckModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        user={user}
        deck={{
          id: 'final-submission-deck',
          name: FINAL_SUBMISSION_DECK_NAME,
          tricks: selectedTrickDetails.map(item => item.trick.name)
        }}
      />
    </div>
  );
};

export default TournamentFinalSubmission;
